import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, Plus, Download, Edit, Trash2, IndianRupee, TrendingUp, TrendingDown, AlertCircle, Settings, Banknote } from 'lucide-react';
import { CustomDatePicker } from '../components/ui/custom-date-picker';
import { getTodayString } from '../utils/dateUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

const initialTransactionData = { amount_sent: '', date: getTodayString(), transaction_id: '', from_account: '', to_account: '' };

export default function Investment() {
  const [investment, setInvestment] = useState({ total_capital_required: 0, transactions: [], total_sent: 0, pending_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [showCapitalDialog, setShowCapitalDialog] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [capitalAmount, setCapitalAmount] = useState('');
  const [transactionData, setTransactionData] = useState(initialTransactionData);
  const [proofFiles, setProofFiles] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isApprover = currentUser.role === 'approver' || currentUser.role === 'super_approver';

  useEffect(() => { fetchInvestment(); }, []);

  const fetchInvestment = async (isRetry = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investment`, { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000
      });
      setInvestment(response.data); setCapitalAmount(response.data.total_capital_required || '');
    } catch (error) {
      console.error('Investment fetch error:', error);
      if (!isRetry && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
        setTimeout(() => fetchInvestment(true), 2000);
        return;
      }
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - check your connection');
      } else {
        toast.error('Failed to load investment data');
      }
    } finally { setLoading(false); }
  };

  const handleSetCapital = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData(); formData.append('total_capital_required', capitalAmount);
      await axios.post(`${API}/investment/capital`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Capital updated'); setShowCapitalDialog(false); fetchInvestment();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
  };

  const resetTransactionForm = () => { setTransactionData(initialTransactionData); setProofFiles([]); setEditingTransaction(null); };
  const openAddTransaction = () => { resetTransactionForm(); setShowTransactionDialog(true); };
  const openEditTransaction = (t) => { setEditingTransaction(t); setTransactionData({ amount_sent: t.amount_sent || '', date: t.date || '', transaction_id: t.transaction_id || '', from_account: t.from_account || '', to_account: t.to_account || '' }); setProofFiles([]); setShowTransactionDialog(true); };

  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const formData = new FormData();
    Object.keys(transactionData).forEach(key => formData.append(key, transactionData[key]));
    proofFiles.forEach(file => formData.append('proof_files', file));
    try {
      if (editingTransaction) { await axios.put(`${API}/investment/transaction/${editingTransaction.id}`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }); toast.success('Transaction updated'); }
      else { await axios.post(`${API}/investment/transaction`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }); toast.success('Transaction added'); }
      setShowTransactionDialog(false); resetTransactionForm(); fetchInvestment();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try { const token = localStorage.getItem('token'); await axios.delete(`${API}/investment/transaction/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted'); fetchInvestment(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const downloadProof = async (transId, index) => {
    try {
      const token = localStorage.getItem('token');
      // Use blob response type to handle R2 file downloads
      const response = await axios.get(`${API}/investment/transaction/${transId}/proof/${index}`, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('File opened');
    } catch (error) { toast.error('Failed to download'); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="flex items-center gap-3 text-slate-500"><div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" /><span>Loading...</span></div></div></Layout>;

  const progress = investment.total_capital_required > 0 ? (investment.total_sent / investment.total_capital_required) * 100 : 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-amber-600 text-sm font-bold tracking-[0.15em] uppercase mb-1">Capital</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>Investment Tracking</h1>
            <p className="text-slate-500 mt-1">Monitor capital requirements and fund transfers</p>
          </div>
          {isApprover && <div className="flex gap-2"><Button onClick={() => setShowCapitalDialog(true)} variant="outline" className="rounded-xl h-11 w-11 p-0" title="Set Capital"><Settings className="w-5 h-5" /></Button><Button data-testid="add-investment-btn" onClick={openAddTransaction} className="h-11 w-11 p-0 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/30" title="Inject Cash"><Banknote className="w-5 h-5" /></Button></div>}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-slate-800 to-slate-900 text-white overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0"><Wallet className="w-4 h-4 sm:w-5 sm:h-5" /></div><span className="text-xs sm:text-sm opacity-80 truncate">Total Required</span></div>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>₹{investment.total_capital_required.toLocaleString('en-IN')}</p>
          </Card>
          <Card className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0"><TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" /></div><span className="text-xs sm:text-sm opacity-80 truncate">Cash Injected</span></div>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>₹{investment.total_sent.toLocaleString('en-IN')}</p>
          </Card>
          <Card data-testid="investment-balance" className="p-4 sm:p-6 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="p-1.5 sm:p-2 bg-white/20 rounded-lg flex-shrink-0"><AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" /></div><span className="text-xs sm:text-sm opacity-80 truncate">Pending</span></div>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>₹{investment.pending_amount.toLocaleString('en-IN')}</p>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="p-6 border-0 rounded-2xl shadow-lg bg-white">
          <div className="flex justify-between text-sm mb-2"><span className="text-slate-600 font-medium">Funding Progress</span><span className="text-slate-900 font-bold">{progress.toFixed(1)}%</span></div>
          <div className="h-4 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500" style={{ width: `${Math.min(progress, 100)}%` }} /></div>
        </Card>

        {/* Transactions */}
        <Card className="p-6 border-0 rounded-2xl shadow-lg bg-white">
          <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>Transaction History</h3>
          {investment.transactions.length === 0 ? (
            <div className="text-center py-12"><div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><IndianRupee className="w-8 h-8 text-slate-400" /></div><p className="text-slate-500">No transactions yet</p></div>
          ) : (
            <div className="space-y-3">{investment.transactions.map((t) => (
              <div key={t.id} className="p-4 bg-slate-50 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-100 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                  <div><p className="font-bold text-slate-900">₹{parseFloat(t.amount_sent).toLocaleString('en-IN')}</p><p className="text-xs text-slate-500">{t.date} • {t.transaction_id}</p><p className="text-xs text-slate-400">{t.from_account} → {t.to_account}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  {t.proof_filenames?.map((f, i) => <Button key={i} size="sm" variant="outline" onClick={() => downloadProof(t.id, i)} className="rounded-lg text-xs"><Download className="w-3 h-3 mr-1" />Proof</Button>)}
                  {isApprover && <><Button size="sm" variant="outline" onClick={() => openEditTransaction(t)} className="rounded-lg"><Edit className="w-4 h-4" /></Button><Button size="sm" variant="outline" onClick={() => handleDeleteTransaction(t.id)} className="rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button></>}
                </div>
              </div>
            ))}</div>
          )}
        </Card>

        {/* Capital Dialog */}
        <Dialog open={showCapitalDialog} onOpenChange={setShowCapitalDialog}>
          <DialogContent><DialogHeader><DialogTitle className="text-xl font-bold">Set Total Capital Required</DialogTitle></DialogHeader>
            <form onSubmit={handleSetCapital} className="space-y-4 mt-4"><div className="space-y-2"><Label className="text-sm font-semibold">Amount (₹)</Label><Input type="number" value={capitalAmount} onChange={(e) => setCapitalAmount(e.target.value)} required className="h-11 rounded-xl" /></div><Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-xl">Save</Button></form>
          </DialogContent>
        </Dialog>

        {/* Transaction Dialog */}
        <Dialog open={showTransactionDialog} onOpenChange={(o) => { if (!o) resetTransactionForm(); setShowTransactionDialog(o); }}>
          <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="text-xl font-bold">{editingTransaction ? 'Edit' : 'Add'} Transaction</DialogTitle></DialogHeader>
            <form onSubmit={handleSaveTransaction} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Amount (₹) *</Label>
                  <Input type="number" value={transactionData.amount_sent} onChange={(e) => setTransactionData({ ...transactionData, amount_sent: e.target.value })} required className="h-11 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Date *</Label>
                  <CustomDatePicker
                    value={transactionData.date}
                    onChange={(date) => setTransactionData({ ...transactionData, date: date || '' })}
                    placeholder="Select date"
                  />
                </div>
              </div>
              <div className="space-y-2"><Label className="text-sm font-semibold">Transaction ID *</Label><Input value={transactionData.transaction_id} onChange={(e) => setTransactionData({ ...transactionData, transaction_id: e.target.value })} required className="h-11 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label className="text-sm font-semibold">From Account *</Label><Input value={transactionData.from_account} onChange={(e) => setTransactionData({ ...transactionData, from_account: e.target.value })} required className="h-11 rounded-xl" /></div><div className="space-y-2"><Label className="text-sm font-semibold">To Account *</Label><Input value={transactionData.to_account} onChange={(e) => setTransactionData({ ...transactionData, to_account: e.target.value })} required className="h-11 rounded-xl" /></div></div>
              <div className="space-y-2"><Label className="text-sm font-semibold">Proof Files</Label><Input type="file" multiple onChange={(e) => setProofFiles(Array.from(e.target.files))} className="h-11 rounded-xl" /></div>
              <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-xl">{editingTransaction ? 'Update' : 'Add'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
